import { useState } from 'react';
import { Box, CircularProgress, Grid, Stack } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { TitlePages } from '@/components/Base/TitlePage';
import type { ScorePedagogicoFilters as FiltersType } from '@/features/score-pedagogico/types';
import ScorePedagogicoFilters from '@/features/score-pedagogico/components/ScorePedagogicoFilters';
import ScorePedagogicoKpis from '@/features/score-pedagogico/components/ScorePedagogicoKpis';
import BarChartDisciplinas from '@/features/score-pedagogico/components/BarChartDisciplinas';
import HeatmapTurmaDisciplina from '@/features/score-pedagogico/components/HeatmapTurmaDisciplina';
import RadarChartTurma from '@/features/score-pedagogico/components/RadarChartTurma';
import LineChartEvolucao from '@/features/score-pedagogico/components/LineChartEvolucao';
import TabelaRankingTurmas from '@/features/score-pedagogico/components/TabelaRankingTurmas';
import LoadingSummary from '@/features/dashboard/components/LoadingSummary';
import {
  useScoreKpis,
  useMediaPorDisciplina,
  useHeatmap,
  useRadar,
  useEvolucaoBimestral,
  useRankingTurmas,
} from '@/features/score-pedagogico/api';

const ScorePedagogico = () => {
  const navigate = useNavigate();
  const [filters, setFilters] = useState<FiltersType>({
    serieNome: null,
    turmaNome: null,
    disciplinaId: null,
    ano: 2026,
  });

  const { data: kpis, isFetching: loadingKpis } = useScoreKpis(filters);
  const { data: barData } = useMediaPorDisciplina(filters);
  const { data: heatmapData } = useHeatmap(filters);
  const { data: radarData } = useRadar(filters.turmaNome, filters.ano);
  const { data: evolucaoData } = useEvolucaoBimestral(filters);
  const { data: rankingData } = useRankingTurmas(filters);

  return (
    <Box padding="15px">
      {/* Header */}
      <Stack flexDirection="row" alignItems="center" mb={1}>
        <ArrowBack
          sx={{ cursor: 'pointer', mr: '10px' }}
          onClick={() => navigate(-1)}
        />
        <TitlePages title="Score Pedagógico" />
      </Stack>

      {/* Filtros */}
      <ScorePedagogicoFilters filters={filters} onFilterChange={setFilters} />

      {/* KPIs */}
      {loadingKpis && !kpis ? <LoadingSummary /> : <ScorePedagogicoKpis data={kpis} />}

      {/* Gráficos linha 1: Barras + Heatmap */}
      <Grid container spacing={2} mt={1}>
        <Grid item xs={24} md={12}>
          {barData ? (
            <BarChartDisciplinas data={barData} />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', background: '#fff', borderRadius: '14px', border: '1px solid rgba(176,176,176,1)' }}>
              <CircularProgress />
            </Box>
          )}
        </Grid>

      </Grid>

      <Grid container spacing={2} mt={1}>
        <Grid item xs={24} md={12}>
          <HeatmapTurmaDisciplina data={heatmapData} />
        </Grid>
      </Grid>


      {/* Gráficos linha 2: Radar + Evolução */}
      <Grid container spacing={2} mt={1}>
        <Grid item xs={12} md={6}>
          <RadarChartTurma data={radarData} hasTurma={!!filters.turmaNome} />
        </Grid>
        <Grid item xs={12} md={6}>
          {evolucaoData ? (
            <LineChartEvolucao data={evolucaoData} />
          ) : (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', background: '#fff', borderRadius: '14px', border: '1px solid rgba(176,176,176,1)' }}>
              <CircularProgress />
            </Box>
          )}
        </Grid>
      </Grid>

      {/* Tabela Ranking */}
      <Box mt={3}>
        {rankingData ? (
          <TabelaRankingTurmas data={rankingData} />
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default ScorePedagogico;
